// openEHR AM model — BMM package org.openehr.am.aom2.constraint_model
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { C_PRIMITIVE } from "../_unassigned.ts";
import type { C_STRING } from "./constraint_model/primitive.ts";
import * as openehr_base from "../../base/mod.ts";

/**
 * Archetype equivalent to LOCATABLE class in openEHR Common reference model. Defines common constraints for any inheritor of LOCATABLE in any reference model.
 */
export abstract class ARCHETYPE_CONSTRAINT {
  /**
   * True if constraints represented by this node, ignoring any sub-parts, are narrower or the same as other.
   * Typically used during validation of special-ised archetype nodes.
   * @param other - Parameter
   * @returns Result value
   */
  is_subset_of(_other: ARCHETYPE_CONSTRAINT): openehr_base.Boolean {
    return openehr_base.Boolean.from(false);
  }

  is_valid(): openehr_base.Boolean {
    return openehr_base.Boolean.from(true);
  }

  /**
   * Path of this node relative to root of archetype.
   * @returns Result value
   */
  path(): openehr_base.String {
    // TODO: Implement path behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method path not yet implemented.");
  }

  /**
   * True if the relative path a_path exists at this node.
   * @param a_path - Parameter
   * @returns Result value
   */
  has_path(a_path: openehr_base.String): openehr_base.Boolean {
    // TODO: Implement has_path behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method has_path not yet implemented.");
  }
}

/**
 * Abstract model of constraint on any kind of object node.
 */
export abstract class C_OBJECT extends ARCHETYPE_CONSTRAINT {
  /**
   * Internal storage for rm_type_name
   * @protected
   */
  protected _rm_type_name?: openehr_base.String;

  /**
   * Reference model type that this node corresponds to.
   */
  get rm_type_name(): string | undefined {
    return this._rm_type_name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_type_name.
   * Use this to access openehr_base.String methods.
   */
  get $rm_type_name(): openehr_base.String | undefined {
    return this._rm_type_name;
  }

  /**
   * Sets rm_type_name from either a primitive value or openehr_base.String wrapper.
   */
  set rm_type_name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_type_name = undefined;
    } else if (typeof val === "string") {
      this._rm_type_name = openehr_base.String.from(val);
    } else {
      this._rm_type_name = val;
    }
  }

  /**
   * Occurrences of this object node in the data, under the owning attribute. Upper limit can only be greater than 1 if owning attribute has a cardinality of more than 1).
   */
  occurrences?: openehr_base.Multiplicity_interval;
  /**
   * Internal storage for node_id
   * @protected
   */
  protected _node_id?: openehr_base.String;

  /**
   * Semantic identifier of this node, used to dis-tinguish sibling nodes. All nodes must have a node_id; for nodes under a container C_ATTRIBUTE, the id must be an id-code must be defined in the archetype terminolo-gy. For valid structures, all node ids are id-codes.
   * For C_PRIMITIVE_OBJECTs, it will have the special value Primitive_node_id.
   */
  get node_id(): string | undefined {
    return this._node_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for node_id.
   * Use this to access openehr_base.String methods.
   */
  get $node_id(): openehr_base.String | undefined {
    return this._node_id;
  }

  /**
   * Sets node_id from either a primitive value or openehr_base.String wrapper.
   */
  set node_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._node_id = undefined;
    } else if (typeof val === "string") {
      this._node_id = openehr_base.String.from(val);
    } else {
      this._node_id = val;
    }
  }
}

/**
 * Abstract model of constraint on any kind of attribute node.
 */
export abstract class C_ATTRIBUTE extends ARCHETYPE_CONSTRAINT {
  /**
   * Internal storage for rm_attribute_name
   * @protected
   */
  protected _rm_attribute_name?: openehr_base.String;

  /**
   * Reference model attribute within the enclosing type represented by a C_OBJECT.
   */
  get rm_attribute_name(): string | undefined {
    return this._rm_attribute_name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_attribute_name.
   * Use this to access openehr_base.String methods.
   */
  get $rm_attribute_name(): openehr_base.String | undefined {
    return this._rm_attribute_name;
  }

  /**
   * Sets rm_attribute_name from either a primitive value or openehr_base.String wrapper.
   */
  set rm_attribute_name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_attribute_name = undefined;
    } else if (typeof val === "string") {
      this._rm_attribute_name = openehr_base.String.from(val);
    } else {
      this._rm_attribute_name = val;
    }
  }

  /**
   * Constraint on every attribute, regardless of whether it is singular or of a container type, which indicates whether its target object exists or not (i.e. is mandatory or not).
   */
  existence?: openehr_base.Multiplicity_interval;
  /**
   * Child C_OBJECT nodes. Each such node represents a constraint on the type of this attribute in its reference model. Multiples occur both for multiple items in the case of container attributes, and alternatives in the case of singular attributes.
   */
  children?: C_OBJECT[];
  /**
   * True if any value (i.e. instance) of the reference model attribute represented by this C_ATTIRBUTE is allowed.
   * @returns Result value
   */
  any_allowed(): openehr_base.Boolean {
    // TODO: Implement any_allowed behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method any_allowed not yet implemented.");
  }
}

/**
 * Abstract parent type of C_OBJECT subtypes that are defined by value, i.e. whose definitions are actually in the archetype rather than being by reference.
 */
export abstract class C_DEFINED_OBJECT extends C_OBJECT {
  /**
   * Value to be assumed if none sent in data.
   */
  assumed_value?: openehr_base.Any;
  /**
   * True if a_value is valid with respect to constraint expressed in concrete instance of this type.
   * @param a_value - Parameter
   * @returns Result value
   */
  valid_value(_a_value: openehr_base.Any): openehr_base.Boolean {
    return openehr_base.Boolean.from(true);
  }

  /**
   * Generate a prototype value from this constraint object.
   * @returns Result value
   */
  prototype_value(): openehr_base.Any {
    if (this.assumed_value !== undefined) {
      return this.assumed_value;
    }
    throw new Error("Method prototype_value not yet implemented.");
  }

  /**
   * True if there is an assumed value.
   *
   * @returns Result value
   */
  has_assumed_value(): openehr_base.Boolean {
    // TODO: Implement has_assumed_value behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method has_assumed_value not yet implemented.");
  }

  /**
   * Generate a default value from this constraint object (runtime computation).
   * @returns Result value
   */
  compute_default_value(): openehr_base.Any {
    if (this.assumed_value !== undefined) {
      return this.assumed_value;
    }
    throw new Error("No assumed value for compute_default_value.");
  }

  /**
   * True if any value of the reference model type being constrained is allowed. Redefine in descendants.
   * @returns Result value
   */
  any_allowed(): openehr_base.Boolean {
    return openehr_base.Boolean.from(false);
  }
}

/**
 * A constraint defined by proxy, using a reference to an object constraint defined elsewhere in the same archetype. Note that since this object refers to another node, there are two objects with available occurrences values. The local occurrences value on a \`COMPLEX_OBJECT_PROXY\` should always be used; when setting this from a serialised form, if no occurrences is mentioned, the target occurrences should be used (not the standard default of \`{1..1}\`); otherwise the locally specified occurrences should be used as normal. When serialising out, if the occurrences is the same as that of the target, it can be left out.
 */
export class C_COMPLEX_OBJECT_PROXY extends C_OBJECT {
  /**
   * Internal storage for target_path
   * @protected
   */
  protected _target_path?: openehr_base.String;

  /**
   * Reference to an object node using archetype path notation.
   */
  get target_path(): string | undefined {
    return this._target_path?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for target_path.
   * Use this to access openehr_base.String methods.
   */
  get $target_path(): openehr_base.String | undefined {
    return this._target_path;
  }

  /**
   * Sets target_path from either a primitive value or openehr_base.String wrapper.
   */
  set target_path(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._target_path = undefined;
    } else if (typeof val === "string") {
      this._target_path = openehr_base.String.from(val);
    } else {
      this._target_path = val;
    }
  }

  /**
   * True if target occurrences are to be used as the value of occurrences in this object; by the time of runtime use, the target occurrences value has to be set into this object.
   * @returns Result value
   */
  use_target_occurrences(): openehr_base.Boolean {
    // TODO: Implement use_target_occurrences behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method use_target_occurrences not yet implemented.");
  }

  /**
   * True if this node occurrences conforms to \`_other.occurrences_\`; \`_other_\` is assumed to be in a flat archetype.
   *
   * If \`_other_\` is a \`C_COMPLEX_OBJECT\`, then always \`True\`, since if occurrences defined on proxy node, it is an override of  the occurrences on the target, and it doesn't have to conform to anything except the containing attribute's cardinality. However, if \`_other_\` is also a \`C_COMPLEX_OBJECT\` then the override is of another use_node, and normal occurrences apply
   *
   * @param other - Parameter
   * @returns Result value
   */
  occurrences_conforms_to(other: C_OBJECT): openehr_base.Boolean {
    // TODO: Implement occurrences_conforms_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method occurrences_conforms_to not yet implemented.");
  }
}

/**
 * Abstract parent type of C_OBJECT subtypes that are defined by reference.
 */
export abstract class C_REFERENCE_OBJECT extends C_OBJECT {
}

/**
 * Constraint describing a  slot' where another archetype can occur.
 */
export class ARCHETYPE_SLOT extends C_REFERENCE_OBJECT {
  /**
   * List of constraints defining other archetypes that could be included at this point.
   */
  includes?: undefined;
  /**
   * List of constraints defining other archetypes that cannot be included at this point.
   */
  excludes?: undefined;
}

/**
 * Constraint on a primitive type.
 */
export class C_PRIMITIVE_OBJECT extends C_DEFINED_OBJECT {
  /**
   * Object actually defining the constraint.
   */
  item?: C_PRIMITIVE;
  /**
   * True if any value of the type being constrained in item is allowed.
   * @returns Result value
   */
  any_allowed(): openehr_base.Boolean {
    // TODO: Implement any_allowed behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method any_allowed not yet implemented.");
  }
}

/**
 * Defines the order indicator that can be used on a \`C_OBJECT\` within a container attribute in a specialised archetype to indicate its order with respect to a sibling defined in a higher specialisation level.
 *
 * Misuse: This type cannot be used on a \`C_OBJECT\` other than one within a container attribute in a specialised archetype.
 */
export class SIBLING_ORDER {
  /**
   * Internal storage for is_before
   * @protected
   */
  protected _is_before?: openehr_base.Boolean;

  /**
   * True if the order relationship is ‘before’, if False, it is ‘after’.
   */
  get is_before(): boolean | undefined {
    return this._is_before?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_before.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_before(): openehr_base.Boolean | undefined {
    return this._is_before;
  }

  /**
   * Sets is_before from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_before(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_before = undefined;
    } else if (typeof val === "boolean") {
      this._is_before = openehr_base.Boolean.from(val);
    } else {
      this._is_before = val;
    }
  }

  /**
   * Internal storage for sibling_node_id
   * @protected
   */
  protected _sibling_node_id?: openehr_base.String;

  /**
   * Node identifier of sibling before or after which this node should come.
   */
  get sibling_node_id(): string | undefined {
    return this._sibling_node_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for sibling_node_id.
   * Use this to access openehr_base.String methods.
   */
  get $sibling_node_id(): openehr_base.String | undefined {
    return this._sibling_node_id;
  }

  /**
   * Sets sibling_node_id from either a primitive value or openehr_base.String wrapper.
   */
  set sibling_node_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._sibling_node_id = undefined;
    } else if (typeof val === "string") {
      this._sibling_node_id = openehr_base.String.from(val);
    } else {
      this._sibling_node_id = val;
    }
  }

  /**
   * True if the order relationship is \`_after_\`, computed as the negation of \`_is_before_\`.
   * @returns Result value
   */
  is_after(): openehr_base.Boolean {
    // TODO: Implement is_after behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method is_after not yet implemented.");
  }
}

/**
 * Abstract parent of classes defining second order constraints.
 */
export abstract class C_SECOND_ORDER {
  /**
   * Members of this second order constrainer. Normally redefined in descendants.
   */
  members: C_ATTRIBUTE[] | C_OBJECT[] = [];
  /**
   * True if constraints represented by this node, ignoring any sub-parts, are narrower or the same as other. Typically used during validation of specialised archetype nodes.
   * @param other - Parameter
   * @param rmcc - Parameter
   * @returns Result value
   */
  abstract c_conforms_to(
    other: C_SECOND_ORDER,
    rmcc: undefined,
  ): openehr_base.Boolean;

  /**
   * True if constraints represented by this node contain no further redefinitions with respect to the node other. Typically used to test if an inherited node locally contains any constraints.
   * @param other - Parameter
   * @returns Result value
   */
  abstract c_congruent_to(other: C_SECOND_ORDER): openehr_base.Boolean;
}

/**
 * Object representing a constraint on an attribute tuple, i.e. a group of attributes that are constrained together. Typically used for representing co-varying constraints like \`{units, range}\` constraints.
 */
export class C_ATTRIBUTE_TUPLE extends C_SECOND_ORDER {
  /**
   * Tuple definitions.
   */
  tuples?: C_PRIMITIVE_TUPLE[];
  /**
   * List of \`C_ATTRIBUTEs\` forming the definition of the tuple.
   */
  override members: C_ATTRIBUTE[] = [];
  /**
   * True if constraints represented by this node, ignoring any sub-parts, are narrower or the same as other. Typically used during validation of specialised archetype nodes.
   * @param other - Parameter
   * @param rmcc - Parameter
   * @returns Result value
   */
  c_conforms_to(
    other: C_ATTRIBUTE_TUPLE,
    rmcc: undefined,
  ): openehr_base.Boolean {
    // TODO: Implement c_conforms_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method c_conforms_to not yet implemented.");
  }

  /**
   * True if constraints represented by this node contain no further redefinitions with respect to the node other. Typically used to test if an inherited node locally contains any constraints.
   * @param other - Parameter
   * @returns Result value
   */
  c_congruent_to(other: C_SECOND_ORDER): openehr_base.Boolean {
    // TODO: Implement c_congruent_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method c_congruent_to not yet implemented.");
  }
}

/**
 * Class representing a single object tuple instance in a tuple constraint. Each such instance is a vector of object constraints, where each member (each \`C_PRIMITIVE_OBJECT\`) corresponds to one of the \`C_ATTRIBUTEs\` referred to by the owning \`C_ATTRIBUTE_TUPLE\`.
 */
export class C_PRIMITIVE_TUPLE extends C_SECOND_ORDER {
  /**
   * Object constraint members of this tuple group.
   */
  override members: C_OBJECT[] = [];
  /**
   * True if constraints represented by this node, ignoring any sub-parts, are narrower or the same as other. Typically used during validation of specialised archetype nodes.
   * @param other - Parameter
   * @param rmcc - Parameter
   * @returns Result value
   */
  c_conforms_to(
    other: C_PRIMITIVE_TUPLE,
    rmcc: undefined,
  ): openehr_base.Boolean {
    // TODO: Implement c_conforms_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method c_conforms_to not yet implemented.");
  }

  /**
   * True if constraints represented by this node contain no further redefinitions with respect to the node other. Typically used to test if an inherited node locally contains any constraints.
   * @param other - Parameter
   * @returns Result value
   */
  c_congruent_to(other: C_SECOND_ORDER): openehr_base.Boolean {
    // TODO: Implement c_congruent_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method c_congruent_to not yet implemented.");
  }
}

/**
 * Constraint on complex objects, i.e. any object that consists of other object constraints.
 */
export class C_COMPLEX_OBJECT extends C_DEFINED_OBJECT {
  /**
   * List of constraints on attributes of the reference model type represented by this object.
   */
  attributes?: C_ATTRIBUTE[];
  /**
   * Co-varying attribute constraints (ADL2 tuple syntax).
   */
  attribute_tuples?: C_ATTRIBUTE_TUPLE[];
  /**
   * True if any value of the reference model type being constrained is allowed.
   * @returns Result value
   */
  any_allowed(): openehr_base.Boolean {
    // TODO: Implement any_allowed behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method any_allowed not yet implemented.");
  }
}

/**
 * A specialisation of \`C_COMPLEX_OBJECT\` whose node_id attribute is an archetype identifier rather than the normal internal node code (i.e. id-code).
 *
 * Used in two situations. The first is to represent an 'external reference' to an archetype from within another archetype or template. This supports re-use. The second use is within a template, where it is used as a slot-filler.
 *
 * For a new external reference, the \`_node_id_\` is set in the normal way, i.e. with a new code at the specialisation level of the archetype.
 *
 * For a slot-filler or a redefined external reference, the \`_node_id_\` is set to a specialised version of the \`_node_id_\` of the node being specialised, allowing matching to occur during flattening.
 *
 * In all uses within source archetypes and templates, the \`_children_\` attribute is \`Void\`.
 *
 * In an operational template, the \`_node_id_\` is converted to the \`_archetype_ref_\`, and the structure contains the result of flattening any template overlay structure and the underlying flat archetype.
 */
export class C_ARCHETYPE_ROOT extends C_COMPLEX_OBJECT {
  /**
   * Internal storage for archetype_ref
   * @protected
   */
  protected _archetype_ref?: openehr_base.String;

  /**
   * Reference to archetype is being used to fill a slot or redefine an external reference. Typically an 'interface' archetype id, i.e. identifier with partial version information.
   */
  get archetype_ref(): string | undefined {
    return this._archetype_ref?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for archetype_ref.
   * Use this to access openehr_base.String methods.
   */
  get $archetype_ref(): openehr_base.String | undefined {
    return this._archetype_ref;
  }

  /**
   * Sets archetype_ref from either a primitive value or openehr_base.String wrapper.
   */
  set archetype_ref(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._archetype_ref = undefined;
    } else if (typeof val === "string") {
      this._archetype_ref = openehr_base.String.from(val);
    } else {
      this._archetype_ref = val;
    }
  }
}

/**
 * Constraint expression representing a regex constraint on an archetype identifier.
 */
export class ARCHETYPE_ID_CONSTRAINT {
  /**
   * Right hand side of the constraint expression, in the form of a \`C_STRING\`, i.e. string value constrainer.
   */
  constraint?: C_STRING;
}
